import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Award, Users, Target, CheckCircle2 } from "lucide-react";

const highlights = [
  "Cybersecurity expertise",
  "Project & program management",
  "Military & veteran leadership",
  "CMMC readiness specialists",
];

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="about-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/50 to-slate-950" />
      
      {/* Decorative line */}
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-slate-800">
              <img
                src="https://customer-assets.emergentagent.com/job_defense-compliance/artifacts/lgc1apbs_image.png"
                alt="Veteran Owned Business - Military Service Member with American Flag"
                className="w-full h-[400px] lg:h-[500px] object-cover object-left"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
              
              {/* Floating card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="absolute bottom-6 left-6 right-6 p-4 bg-slate-900/90 backdrop-blur-sm rounded-xl border border-slate-700"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <Award className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Veteran-Led & Owned</p>
                    <p className="text-slate-400 text-sm">Service Disabled Veteran Owned Small Business</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 border border-[#D4AF37]/20 rounded-xl -z-10" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 border border-[#D4AF37]/10 rounded-xl -z-10" />
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-block text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4">
              About Us
            </span>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
              Decades of Frontline Expertise
            </h2>
            
            <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-8">
              We are a veteran-led and owned company. With over 25 years of combined 
              military and defense experience, we bring a unique perspective to complex security 
              landscapes because we don&apos;t just consult on requirements; we&apos;ve lived them.
            </p>
            
            <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-8">
              Our team delivers the technical depth and strategic foresight necessary 
              to navigate the most rigorous compliance mandates.
            </p>

            {/* Highlights */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {highlights.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{item}</span>
                </motion.div>
              ))}
            </div>

            {/* Stats mini */}
            <div className="flex flex-wrap gap-6 pt-6 border-t border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white font-bold">Expert Team</p>
                  <p className="text-slate-500 text-xs">Certified Professionals</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                  <Target className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white font-bold">Mission Focused</p>
                  <p className="text-slate-500 text-xs">Results Driven</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
